import { useMutation } from "@tanstack/react-query"
import { useCallback, useState } from "react"
import { getSession, getAccessToken } from "src/redux/slicers/userData"

// API endpoint - configurable via environment variable
const CHAT_API_URL =
  "https://bedrock-agentcore.us-east-1.amazonaws.com/runtimes/arn%3Aaws%3Abedrock-agentcore%3Aus-east-1%3A004819182232%3Aruntime%2Fchat_server_v2_yceuhyn4mreaxg5b7bcr2yjqqy-CvNiaVCp2f/invocations?qualifier=DEFAULT"

// Type definitions (same as GraphQL version)
interface StartChatVariables {
  message: string
  //buildingId: string
  sessionId: string
}

interface StartChatResponse {
  sessionId: string
  //buildingId: string
  status: string
  message?: string
}

// Custom hook - handles SSE streaming response
export function useStartChat() {
  const [streamedMessage, setStreamedMessage] = useState<string>("")
  const [isStreaming, setIsStreaming] = useState<boolean>(false)

  const mutation = useMutation({
    mutationFn: async (
      variables: StartChatVariables
    ): Promise<StartChatResponse> => {
      const idToken = await getSession(true)
      const accessToken = await getAccessToken()

      setStreamedMessage("")
      setIsStreaming(true)

      const response = await fetch(CHAT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          Accept: "text/event-stream"
        },
        body: JSON.stringify({
          message: variables.message,
          // buildingId: variables.buildingId,
          sessionId: variables.sessionId,
          id_token: idToken
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error("API Error:", errorText)
        setIsStreaming(false)
        throw new Error(
          `HTTP error! status: ${response.status}, message: ${errorText}`
        )
      }

      // Handle SSE streaming response
      const reader = response.body?.getReader()
      const decoder = new TextDecoder()
      let fullMessage = ""
      let buffer = ""

      if (reader) {
        try {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break

            const chunk = decoder.decode(value, { stream: true })
            buffer += chunk

            // Process complete lines from buffer
            const lines = buffer.split("\n")
            // Keep the last potentially incomplete line in buffer
            buffer = lines.pop() || ""

            for (const line of lines) {
              const trimmedLine = line.trim()

              if (trimmedLine.startsWith("data: ")) {
                const data = trimmedLine.slice(6)

                if (data === "[DONE]") {
                  // Stream complete
                  setIsStreaming(false)
                  continue
                }

                try {
                  const parsed = JSON.parse(data)
                  // Handle both "text" and "message" properties
                  const content = parsed.text ?? parsed.message
                  if (content !== undefined) {
                    fullMessage += content
                    setStreamedMessage(fullMessage)
                  }
                } catch {
                  // If not JSON, treat as plain text chunk
                  if (data && data !== "[DONE]") {
                    fullMessage += data
                    setStreamedMessage(fullMessage)
                  }
                }
              } else if (trimmedLine.startsWith("message:")) {
                // Handle "message: text" format
                const text = trimmedLine.slice(8).trim()
                if (text && text !== "[DONE]") {
                  fullMessage += text
                  setStreamedMessage(fullMessage)
                }
              }
            }
          }

          // Process any remaining buffer content
          if (buffer.trim()) {
            const trimmedBuffer = buffer.trim()
            if (trimmedBuffer.startsWith("data: ")) {
              const data = trimmedBuffer.slice(6)
              if (data !== "[DONE]") {
                try {
                  const parsed = JSON.parse(data)
                  // Handle both "text" and "message" properties
                  const content = parsed.text ?? parsed.message
                  if (content !== undefined) {
                    fullMessage += content
                    setStreamedMessage(fullMessage)
                  }
                } catch {
                  fullMessage += data
                  setStreamedMessage(fullMessage)
                }
              }
            }
          }
        } finally {
          reader.releaseLock()
        }
      }
      setIsStreaming(false)

      return {
        sessionId: variables.sessionId,
        // buildingId: variables.buildingId,
        status: "completed",
        message: fullMessage
      }
    },
    onError: (error) => {
      console.error("Error starting chat:", error)
      setIsStreaming(false)
    }
  })

  const startChat = useCallback(
    async (variables: StartChatVariables) => {
      try {
        const result = await mutation.mutateAsync(variables)
        return result
      } catch (err) {
        console.error("Error starting chat:", err)
        throw err
      }
    },
    [mutation]
  )

  return {
    startChat,
    data: mutation.data,
    streamedMessage,
    isLoading: mutation.isPending,
    isStreaming,
    error: mutation.error,
    isError: mutation.isError,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset
  }
}

